import React, { useEffect, useMemo, useRef, useState } from "react";
import AllSubTreatmentTr from "./AllSubTreatmentTr";
import AddSubTreatmentModel from "./AddSubTreatmentModel";
import {
  getLongTermBalancePayable,
  getSubTreatmentPaidAmount,
  getSubTreatmentPaymentReceivedAmount,
  getTreatmentDueAmount,
} from "./treatmentItemUtils";

const CASE_HEADERS = [
  "Date",
  "Treatment",
  "Tooth No",
  "Qty",
  "Price",
  "Status",
  "Dentist",
  "Note",
  "Action",
];

const getSubTreatmentTotal = (subTreatments: any[]) =>
  subTreatments.reduce((sum, sub) => {
    const price = parseFloat(sub?.price);
    return sum + (Number.isFinite(price) ? price : 0);
  }, 0);

interface CaseTreatmentSectionProps {
  item: any;
  allTeatment: any[];
  setallTeatment: (value: any) => void;
  refresh: () => void;
  showAddForm: boolean;
  onCloseAddForm: () => void;
}

function CaseTreatmentSection({
  item,
  allTeatment,
  setallTeatment,
  refresh,
  showAddForm,
  onCloseAddForm,
}: CaseTreatmentSectionProps) {
  const subTreatments = useMemo(
    () =>
      [...(item?.subTreatment ?? [])].sort(
        (a: any, b: any) =>
          new Date(a?.createdAt).getTime() - new Date(b?.createdAt).getTime()
      ),
    [item?.subTreatment]
  );
  const [editingSubId, setEditingSubId] = useState<string | null>(null);
  const editingSub = useMemo(
    () =>
      subTreatments.find(
        (sub: any) => String(sub?._id) === String(editingSubId)
      ) ?? null,
    [subTreatments, editingSubId]
  );
  const hasContent =
    subTreatments.length > 0 || showAddForm || Boolean(editingSub);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isLongTermParent = item?.status === "long term";
  const longTermEstimate = parseFloat(item?.price) || 0;
  const subTreatmentTotal = useMemo(
    () =>
      isLongTermParent
        ? getSubTreatmentPaidAmount(subTreatments)
        : getSubTreatmentTotal(subTreatments),
    [isLongTermParent, subTreatments]
  );
  const paymentReceived = useMemo(
    () => getSubTreatmentPaymentReceivedAmount(subTreatments),
    [subTreatments]
  );
  const dueAmount = useMemo(
    () => getTreatmentDueAmount(subTreatmentTotal, paymentReceived),
    [subTreatmentTotal, paymentReceived]
  );
  const longTermBalancePayable = useMemo(
    () => getLongTermBalancePayable(longTermEstimate, subTreatmentTotal),
    [longTermEstimate, subTreatmentTotal]
  );

  useEffect(() => {
    if ((showAddForm || editingSub) && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [showAddForm, editingSub]);

  useEffect(() => {
    if (showAddForm) setEditingSubId(null);
  }, [showAddForm]);

  const closeEditForm = () => setEditingSubId(null);

  const handleEditSubTreatment = (sub: any) => {
    onCloseAddForm();
    setEditingSubId(String(sub._id));
  };

  if (!hasContent) return null;

  return (
    <tr>
      <td colSpan={9} className="p-0 px-4 pb-3">
        <div
          ref={sectionRef}
          className="my-1.5 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden"
        >
          <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600 flex items-center justify-between gap-3">
            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">
              Case Treatment
            </h4>
            {(isLongTermParent || subTreatments.length > 0) && (
              <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-sm font-bold whitespace-nowrap">
                {isLongTermParent && (
                  <span className="text-violet-800 dark:text-violet-300">
                    Estimation: Rs {longTermEstimate.toLocaleString()}
                  </span>
                )}
                {subTreatments.length > 0 && (
                  <span
                    className={
                      isLongTermParent
                        ? "text-emerald-800 dark:text-emerald-300"
                        : "text-slate-800 dark:text-slate-200"
                    }
                  >
                    {isLongTermParent ? "Treatment Cost" : "Total"}: Rs{" "}
                    {subTreatmentTotal.toLocaleString()}
                  </span>
                )}
                {(isLongTermParent || paymentReceived > 0) && (
                  <span className="text-sky-800 dark:text-sky-300">
                    Paid: Rs {paymentReceived.toLocaleString()}
                  </span>
                )}
                {isLongTermParent && (
                  <span
                    className={
                      longTermBalancePayable > 0
                        ? "text-amber-800 dark:text-amber-300"
                        : "text-emerald-800 dark:text-emerald-300"
                    }
                  >
                    Balance Payable: Rs {longTermBalancePayable.toLocaleString()}
                  </span>
                )}
                {(isLongTermParent || dueAmount > 0 || paymentReceived > 0) && (
                  <span className="text-red-600 dark:text-red-400">
                    Due: Rs {dueAmount.toLocaleString()}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/40">
                  {CASE_HEADERS.map((header) => (
                    <th
                      key={header}
                      className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {subTreatments.map((sub: any, i: number) =>
                  String(editingSubId) === String(sub?._id) && editingSub ? (
                    <AddSubTreatmentModel
                      key={`edit-${sub?._id ?? i}`}
                      inlineRow
                      nested
                      TreatmentId={item._id}
                      editItem={editingSub}
                      setSubTreatmentOpen={closeEditForm}
                      allTeatment={allTeatment}
                      setallTeatment={setallTeatment}
                      onClose={closeEditForm}
                      onSaved={refresh}
                    />
                  ) : (
                    <AllSubTreatmentTr
                      key={sub?._id ?? i}
                      allTeatment={allTeatment}
                      setallTeatment={setallTeatment}
                      items={sub}
                      item={item}
                      paid={!item?.paid}
                      refresh={refresh}
                      _id={item?._id}
                      nested
                      onEdit={() => handleEditSubTreatment(sub)}
                    />
                  )
                )}
                {showAddForm && !editingSubId && (
                  <AddSubTreatmentModel
                    inlineRow
                    nested
                    TreatmentId={item._id}
                    setSubTreatmentOpen={onCloseAddForm}
                    allTeatment={allTeatment}
                    setallTeatment={setallTeatment}
                    onClose={onCloseAddForm}
                    onSaved={refresh}
                  />
                )}
              </tbody>
            </table>
          </div>
        </div>
      </td>
    </tr>
  );
}

export default CaseTreatmentSection;
